import React, { useEffect } from "react"
import { Card, CardBody, Button, Input, Label } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { submitBazaarEnquiry } from "../../redux/actions/pvs"
import { SUBMIT_BAZAAR_ENQUIRY_RESET } from "../../redux/types/pvs"

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

const heardFromOptions = [
  "Instagram",
  "Facebook",
  "Newspaper",
  "Hoardings",
  "SMS",
  "WhatsApp Message",
  "Other",
]

const BazaarEnquiryForm = ({ backPath = "/volunteer/bazaars" }) => {
  document.title = "Bazaar Walk-In | PULA"

  const query = useQuery()
  const enc_bazaar_id = query.get("id")
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const bazaarList = useSelector(state => state.pvs.bazaarList) || []
  const bazaar = bazaarList.find(b => b.enc_bazaar_id === enc_bazaar_id)

  const submitEnquiryScreen = useSelector(
    state => state.screens.pvs.submitBazaarEnquiry
  )

  const formik = useFormik({
    initialValues: {
      prospect_name: "",
      mobile_no: "",
      heard_from: "",
      accompanying_count: 1,
    },
    validationSchema: Yup.object({
      accompanying_count: Yup.number()
        .typeError("Enter a valid number")
        .integer("Enter a whole number")
        .min(1, "Minimum is 1")
        .max(20, "Maximum is 20")
        .required("Number of people accompanying is required"),
      prospect_name: Yup.string().required("Name is required"),
      mobile_no: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
        .required("Mobile number is required"),
      heard_from: Yup.string().required(
        "Please select where they heard about us"
      ),
    }),
    onSubmit: values => {
      dispatch(
        submitBazaarEnquiry({
          enc_bazaar_id,
          prospect_name: values.prospect_name,
          mobile_no: values.mobile_no,
          heard_from: values.heard_from,
          accompanying_count: Number(values.accompanying_count),
        })
      )
    },
  })

  useEffect(() => {
    if (submitEnquiryScreen.success) {
      toast.success(submitEnquiryScreen.message)
      formik.resetForm()
      dispatch({ type: SUBMIT_BAZAAR_ENQUIRY_RESET })
    }
    if (submitEnquiryScreen.error) {
      if (submitEnquiryScreen.alreadyCaptured) {
        toast.info(submitEnquiryScreen.error)
        formik.resetForm()
      } else {
        toast.error(submitEnquiryScreen.error)
      }
      dispatch({ type: SUBMIT_BAZAAR_ENQUIRY_RESET })
    }
  }, [submitEnquiryScreen])

  if (!enc_bazaar_id) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <p className="text-danger">No bazaar selected.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Button
          color="link"
          className="ps-0 mb-3"
          onClick={() => navigate(backPath)}
        >
          <i className="dripicons-arrow-thin-left"></i> Back to Bazaars
        </Button>

        <h4 className="h4 mb-3 nom-title">
          {bazaar ? bazaar.bazaar_name : "Bazaar Walk-In"}
        </h4>

        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <div className="mb-3">
                <Label htmlFor="prospect_name">Name *</Label>
                <Input
                  id="prospect_name"
                  name="prospect_name"
                  type="text"
                  placeholder="Enter prospect's name"
                  value={formik.values.prospect_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.prospect_name &&
                    !!formik.errors.prospect_name
                  }
                />
                {formik.touched.prospect_name &&
                  formik.errors.prospect_name && (
                    <div className="text-danger small mt-1">
                      {formik.errors.prospect_name}
                    </div>
                  )}
              </div>

              <div className="mb-3">
                <Label htmlFor="mobile_no">Mobile Number *</Label>
                <Input
                  id="mobile_no"
                  name="mobile_no"
                  type="text"
                  placeholder="Enter mobile number"
                  value={formik.values.mobile_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.mobile_no && !!formik.errors.mobile_no
                  }
                />
                {formik.touched.mobile_no && formik.errors.mobile_no && (
                  <div className="text-danger small mt-1">
                    {formik.errors.mobile_no}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <Label htmlFor="heard_from">
                  Where did you hear about us? *
                </Label>
                <Input
                  id="heard_from"
                  name="heard_from"
                  type="select"
                  value={formik.values.heard_from}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.heard_from && !!formik.errors.heard_from
                  }
                >
                  <option value="">Select an option</option>
                  {heardFromOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Input>
                {formik.touched.heard_from && formik.errors.heard_from && (
                  <div className="text-danger small mt-1">
                    {formik.errors.heard_from}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <Label htmlFor="accompanying_count">
                  Number of people accompanying *
                </Label>
                <Input
                  id="accompanying_count"
                  name="accompanying_count"
                  type="number"
                  min={1}
                  max={20}
                  value={formik.values.accompanying_count}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.accompanying_count &&
                    !!formik.errors.accompanying_count
                  }
                />
                {formik.touched.accompanying_count &&
                  formik.errors.accompanying_count && (
                    <div className="text-danger small mt-1">
                      {formik.errors.accompanying_count}
                    </div>
                  )}
              </div>

              <Button
                type="submit"
                color="primary"
                disabled={submitEnquiryScreen.loading}
              >
                {submitEnquiryScreen.loading ? "Submitting..." : "Submit Walk-In"}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default BazaarEnquiryForm
